import type {
  TableName,
  ProposedEdit,
  DoNotMerge,
  ExcludedUrl,
  PrioritizeListing,
  AddressRename,
  AllowedWebsiteDomain,
  BuildingOverride,
  DoMergeAddress,
  SightmapUrl,
  BaseManualFix
} from 'common'

import { defineStore } from 'pinia'
import dataPipelineManualFixesApi from '@/api/datapipelinemanualfixes'
import auth0Plugin, { isDataPipelineOverrideAdminEmail } from '@/setups/authSetup'

export const useDataPipelineManualEditsStore = defineStore('datapipelinemanualedits', {
  state: () => ({
    allData: {
      do_not_merge: [] as DoNotMerge[],
      do_merge_addresses: [] as DoMergeAddress[],
      excluded_urls: [] as ExcludedUrl[],
      prioritize_listing: [] as PrioritizeListing[],
      address_rename: [] as AddressRename[],
      allowed_website_domains: [] as AllowedWebsiteDomain[],
      building_overrides: [] as BuildingOverride[],
      sightmap_urls: [] as SightmapUrl[],
      proposed_edits: [] as ProposedEdit[]
    } as Record<TableName, BaseManualFix[]>,
    loading: false,
    canEdit: false
  }),

  actions: {
    getApiResourceName(tableName: string) {
      const apiMap: Record<string, string> = {
        do_not_merge: 'DoNotMerge',
        excluded_urls: 'ExcludedUrl',
        prioritize_listing: 'PrioritizeListing',
        address_rename: 'AddressRename',
        allowed_website_domains: 'AllowedWebsiteDomain',
        building_overrides: 'BuildingOverride',
        do_merge_addresses: 'DoMergeAddress',
        sightmap_urls: 'SightmapUrl',
        proposed_edits: 'ProposedEdit'
      }
      return apiMap[tableName] || ''
    },

    async fetchTabData(tableName: TableName, retryCount = 0): Promise<void> {
      const resourceName = this.getApiResourceName(tableName)
      // Special cases for pluralization in the API list methods
      let listMethodName: keyof typeof dataPipelineManualFixesApi
      if (resourceName === 'DoMergeAddress') {
        listMethodName = 'listDoMergeAddresses'
      } else if (resourceName === 'ProposedEdit') {
        listMethodName = 'listProposedEdits'
      } else {
        const pluralResources = [
          'ExcludedUrl',
          'AllowedWebsiteDomain',
          'BuildingOverride',
          'SightmapUrl'
        ]
        listMethodName = (
          pluralResources.includes(resourceName) ? `list${resourceName}s` : `list${resourceName}`
        ) as keyof typeof dataPipelineManualFixesApi
      }

      try {
        const method = dataPipelineManualFixesApi[listMethodName] as () => Promise<BaseManualFix[]>
        const data = await method()
        this.allData[tableName] = data
      } catch (error) {
        console.error(`Error fetching data for ${tableName} (attempt ${retryCount + 1}):`, error)

        if (retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000
          await new Promise((resolve) => setTimeout(resolve, delay))
          return this.fetchTabData(tableName, retryCount + 1)
        }

        if (!this.allData[tableName]) {
          this.allData[tableName] = []
        }
        throw error
      }
    },

    async fetchAllData(tabs: { name: string }[]) {
      this.loading = true
      try {
        this.canEdit = isDataPipelineOverrideAdminEmail(auth0Plugin.user.value?.email)

        for (const tab of tabs) {
          try {
            await this.fetchTabData(tab.name as TableName)
          } catch {
            console.error(`Error fetching data for ${tab.name}`)
          }
        }
      } finally {
        this.loading = false
      }
    },

    async saveEntry(
      tableName: TableName,
      isEditing: boolean,
      form: Partial<BuildingOverride> | Partial<SightmapUrl> | Record<string, unknown>,
      editingRow?: BaseManualFix
    ) {
      const resourceName = this.getApiResourceName(tableName)
      if (isEditing) {
        if (!editingRow) {
          throw new Error('editingRow is required when isEditing is true')
        }
        const updateMethodName = `update${resourceName}` as keyof typeof dataPipelineManualFixesApi
        const where = this.getWhereClause(tableName, editingRow)

        let updates: Record<string, unknown> = {}
        // We'll pass the cleaned updates from the component for now to keep the store simple,
        // or we could move the cleaning logic here if we had the field definitions.
        // For now, let's assume the component provides the correct updates.
        updates = form as Record<string, unknown>

        const method = dataPipelineManualFixesApi[updateMethodName] as (arg: {
          where: any
          updates: any
        }) => Promise<void>
        await method({
          where,
          updates
        })
      } else {
        const insertMethodName = `insert${resourceName}` as keyof typeof dataPipelineManualFixesApi
        const method = dataPipelineManualFixesApi[insertMethodName] as (arg: any) => Promise<void>
        await method(form)
      }
    },

    // Move helper methods that don't depend on Vue instance
    getWhereClause(tableName: TableName, row: BaseManualFix): Record<string, any> {
      switch (tableName) {
        case 'do_not_merge': {
          const r = row as DoNotMerge
          return {
            id: r.id
          }
        }
        case 'excluded_urls': {
          const r = row as ExcludedUrl
          return { url: r.url }
        }
        case 'prioritize_listing': {
          const r = row as PrioritizeListing
          return {
            building_id: r.building_id,
            listing_source: r.listing_source
          }
        }
        case 'address_rename': {
          const r = row as AddressRename
          return { id: r.id }
        }
        case 'allowed_website_domains': {
          const r = row as AllowedWebsiteDomain
          return { id: r.id }
        }
        case 'building_overrides': {
          const r = row as BuildingOverride
          return { id: r.id }
        }
        case 'do_merge_addresses': {
          const r = row as DoMergeAddress
          return { merge_group_id: r.merge_group_id, address: r.address }
        }
        case 'sightmap_urls': {
          const r = row as SightmapUrl
          return { widget_hash_id: r.widget_hash_id }
        }
        case 'proposed_edits': {
          const r = row as ProposedEdit
          return { id: r.id }
        }
        default: {
          throw new Error(`Unknown table: ${tableName as string}`)
        }
      }
    },

    async deleteEntry(tableName: TableName, row: BaseManualFix) {
      const where = this.getWhereClause(tableName, row)
      const resourceName = this.getApiResourceName(tableName)
      const deleteMethodName = `delete${resourceName}` as keyof typeof dataPipelineManualFixesApi

      if (tableName === 'sightmap_urls') {
        const r = where as { widget_hash_id: string }
        await dataPipelineManualFixesApi.deleteSightmapUrl(r.widget_hash_id)
      } else if (tableName === 'do_not_merge') {
        const r = where as { id: string }
        await dataPipelineManualFixesApi.deleteDoNotMerge(r.id)
      } else if (tableName === 'prioritize_listing') {
        const r = where as { building_id: string; listing_source: string }
        await dataPipelineManualFixesApi.deletePrioritizeListing(r.building_id, r.listing_source)
      } else if (tableName === 'address_rename') {
        const r = where as { id: string }
        await dataPipelineManualFixesApi.deleteAddressRename(r.id)
      } else if (tableName === 'do_merge_addresses') {
        const r = where as { merge_group_id: string; address: string }
        await dataPipelineManualFixesApi.deleteDoMergeAddress(r.merge_group_id, r.address)
      } else {
        const deleteArg = Object.values(where)[0]
        const method = dataPipelineManualFixesApi[deleteMethodName] as (
          arg: unknown
        ) => Promise<void>
        await method(deleteArg)
      }
    },

    async approveProposedEdit(proposalId: string) {
      await dataPipelineManualFixesApi.approveProposedEdit(proposalId)
    },

    async deleteProposedEdit(proposalId: string) {
      await dataPipelineManualFixesApi.deleteProposedEdit(proposalId)
    },

    async insertDoNotMerge(body: { id: string; address1: string; address2: string }) {
      await dataPipelineManualFixesApi.insertDoNotMerge(body)
    },

    async insertDoMergeAddress(body: { merge_group_id: string; address: string }) {
      await dataPipelineManualFixesApi.insertDoMergeAddress(body)
    },

    async updateSightmapUrl(payload: {
      where: { widget_hash_id: string }
      updates: Partial<SightmapUrl>
    }) {
      await dataPipelineManualFixesApi.updateSightmapUrl(payload)
    },

    async insertSightmapUrl(body: Omit<SightmapUrl, 'created_on' | 'created_by'>) {
      await dataPipelineManualFixesApi.insertSightmapUrl(body)
    }
  }
})
